"use client";

import { useRef } from "react";
import { ImagePlus, Loader2, Search, X } from "lucide-react";
import type { PickedImage } from "@/comparison/types";
import { Button } from "@/components/ui/button";
import { cn } from "@/utils/ui";

/** Upload-or-search tile used for the option images, background and watermark. */
export function ImageSlot({
	image,
	placeholder,
	hint,
	busy,
	error,
	onFile,
	onSearch,
	onClear,
	className,
}: {
	image: PickedImage | null;
	placeholder: string;
	/** One line under the buttons — what this image is actually for. */
	hint?: string;
	busy?: boolean;
	error?: string | null;
	onFile: (file: File) => void;
	onSearch?: () => void;
	onClear: () => void;
	className?: string;
}) {
	const inputRef = useRef<HTMLInputElement>(null);

	return (
		<div className="flex flex-col gap-1.5">
			<div
				className={cn(
					// `min-h-32` is a floor, not a size: the empty state is a caption over
					// a row of buttons, and on a narrow column those buttons wrap to two
					// rows and need ~118px. Callers that size the tile by aspect ratio get
					// less than that on a phone, and the buttons spilled out of the box.
					"border-border bg-muted/40 relative flex min-h-32 flex-col items-center justify-center gap-2 rounded-lg border border-dashed p-3",
					error ? "border-destructive/60" : "",
					className,
				)}
				onDragOver={(event) => event.preventDefault()}
				onDrop={(event) => {
					event.preventDefault();
					const file = event.dataTransfer.files?.[0];
					if (file?.type.startsWith("image/")) onFile(file);
				}}
			>
				<input
					ref={inputRef}
					type="file"
					accept="image/*"
					className="hidden"
					onChange={(event) => {
						const file = event.target.files?.[0];
						if (file) onFile(file);
						event.target.value = "";
					}}
				/>

				{image ? (
					<>
						{/* eslint-disable-next-line @next/next/no-img-element -- object URL */}
						<img
							src={image.previewUrl}
							alt=""
							className="size-full max-h-full object-contain"
						/>
						{/* Swapping an image used to mean clearing it first, which read as
						    "search doesn't work on a filled slot". */}
						<div className="absolute top-1.5 right-1.5 flex gap-1">
							{onSearch ? (
								<Button
									type="button"
									variant="secondary"
									size="icon"
									aria-label="Search for a different image"
									onClick={onSearch}
								>
									<Search className="size-3.5" />
								</Button>
							) : null}
							<Button
								type="button"
								variant="secondary"
								size="icon"
								aria-label="Remove this image"
								onClick={onClear}
							>
								<X className="size-3.5" />
							</Button>
						</div>
					</>
				) : (
					<>
						<p className="text-muted-foreground text-center text-xs">
							{placeholder}
						</p>
						<div className="flex flex-wrap justify-center gap-1.5">
							<Button
								type="button"
								variant="outline"
								size="sm"
								disabled={busy}
								onClick={() => inputRef.current?.click()}
							>
								<ImagePlus className="size-3.5" />
								Upload
							</Button>
							{onSearch ? (
								<Button
									type="button"
									variant="outline"
									size="sm"
									disabled={busy}
									onClick={onSearch}
								>
									<Search className="size-3.5" />
									Search
								</Button>
							) : null}
						</div>
						{hint ? (
							<p className="text-muted-foreground/80 text-center text-[11px]">
								{hint}
							</p>
						) : null}
					</>
				)}

				{busy ? (
					<span className="bg-background/70 absolute inset-0 flex items-center justify-center rounded-lg">
						<Loader2 className="text-primary size-5 animate-spin" />
					</span>
				) : null}
			</div>

			{error ? <p className="text-destructive text-xs">{error}</p> : null}
		</div>
	);
}
